import { useState, useEffect } from 'react';
import { SectionWrapper } from '../SectionWrapper';
import { Users, Calendar, MapPin, CreditCard, Building2, FileText, CheckCircle2, AlertCircle } from 'lucide-react';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import { cn } from '@/lib/utils';
import { formatPercent } from '@/lib/formatUtils';

// Types
type PayrollFrequency = 'weekly' | 'bi_weekly' | 'semi_monthly' | 'monthly';
type LocationType = 'single' | 'multi';
type PaymentMethod = 'direct_deposit' | 'paper_check' | 'cash' | 'zelle' | 'other';
type EmployeeType = 'w2' | '1099';
type DataSourceType = 'internal_sheet' | 'pos' | 'payroll_service' | 'other';

interface PaymentMethodConfig {
  method: PaymentMethod;
  type: EmployeeType;
}

interface LocationEmployeeCount {
  locationId: string;
  count: number;
}

interface MultiLocationAllocation {
  employeeId: string;
  locations: string[];
  percentages: Record<string, number>;
}

interface PaymentSourceConfig {
  locationId: string;
  accountIds: string[];
  percentages?: Record<string, number>;
}

interface PayrollSetupData {
  // Section 1
  payrollFrequency?: PayrollFrequency;
  
  // Section 2
  locationType?: LocationType;
  singleLocationEmployeeCount?: number;
  locationEmployeeCounts?: LocationEmployeeCount[];
  multiLocationEmployeeCount?: number;
  multiLocationAllocations?: MultiLocationAllocation[];
  
  // Section 3
  paymentMethods?: PaymentMethodConfig[];
  
  // Section 4
  paymentSources?: PaymentSourceConfig[];
  
  // Section 5
  dataSourceType?: DataSourceType;
  dataSourceConfig?: any;
}

const PAYROLL_FREQUENCIES: { value: PayrollFrequency; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi_weekly', label: 'Bi-weekly' },
  { value: 'semi_monthly', label: 'Semi-monthly' },
  { value: 'monthly', label: 'Monthly' },
];

const PAYMENT_METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'direct_deposit', label: 'Direct Deposit / ACH' },
  { value: 'paper_check', label: 'Paper Check' },
  { value: 'cash', label: 'Cash' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'other', label: 'Other (Manual)' },
];

const POS_PLATFORMS = [
  { value: 'toast', label: 'Toast' },
  { value: 'square', label: 'Square' },
  { value: 'clover', label: 'Clover' },
  { value: 'lightspeed', label: 'Lightspeed' },
  { value: 'other', label: 'Other' },
];

const PAYROLL_SERVICES = [
  { value: 'quickbooks', label: 'QuickBooks' },
  { value: 'adp', label: 'ADP' },
  { value: 'gusto', label: 'Gusto' },
  { value: 'paychex', label: 'Paychex' },
  { value: 'run_by_adp', label: 'Run by ADP' },
  { value: 'other', label: 'Other' },
];

export const PayrollSetupSection = () => {
  const { completeSection, skipSection, updateSectionData } = useOnboarding();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [setupData, setSetupData] = useState<PayrollSetupData>({});
  const [locations, setLocations] = useState<any[]>([]);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const totalSteps = 6;

  useEffect(() => {
    const loadData = async () => {
      setInitialLoading(true);
      await Promise.all([loadInitialData(), loadSavedData()]);
      setInitialLoading(false);
    };
    loadData();
  }, []);

  const loadInitialData = async () => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      // Load locations
      const { data: locationsData } = await supabase
        .from('locations')
        .select('id, location_name, concept_name')
        .eq('company_id', companyId)
        .eq('is_active', true);

      if (locationsData) {
        setLocations(locationsData);
      }

      // Load bank accounts
      const { data: accountsData } = await supabase
        .from('bank_accounts')
        .select('id, account_name, bank_name')
        .eq('company_id', companyId)
        .eq('is_active', true);

      if (accountsData) {
        setBankAccounts(accountsData);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const loadSavedData = async () => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      const { data } = await supabase
        .from('payroll_setup')
        .select('*')
        .eq('company_id', companyId)
        .single();

      if (data) {
        setSetupData({
          payrollFrequency: data.payroll_frequency,
          locationType: data.location_type,
          singleLocationEmployeeCount: data.employee_counts?.single,
          locationEmployeeCounts: Object.entries(data.employee_counts || {}).map(([locationId, count]) => ({
            locationId,
            count: count as number,
          })),
          multiLocationEmployeeCount: data.multi_location_employee_count,
          paymentMethods: data.payment_methods,
          paymentSources: Object.entries(data.payment_sources || {}).map(([locationId, config]) => ({
            locationId,
            accountIds: (config as any).account_ids || [],
            percentages: (config as any).percentages,
          })),
          dataSourceType: data.data_source_type,
          dataSourceConfig: data.data_source_config,
        });
      }
    } catch (error) {
      console.error('Error loading saved data:', error);
    }
  };

  const updateSetupData = (updates: Partial<PayrollSetupData>) => {
    setSetupData(prev => ({ ...prev, ...updates }));
    // Auto-save to onboarding progress
    updateSectionData('payroll', { ...setupData, ...updates });
  };

  const handleNext = async () => {
    // Validate current step before proceeding
    if (!validateCurrentStep()) {
      return;
    }

    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Final step - save and complete
      await saveAndComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 1:
        if (!setupData.payrollFrequency) {
          toast({
            title: 'Validation Error',
            description: 'Please select a payroll frequency',
            variant: 'destructive',
          });
          return false;
        }
        return true;

      case 2:
        if (!setupData.locationType) {
          toast({
            title: 'Validation Error',
            description: 'Please select location type',
            variant: 'destructive',
          });
          return false;
        }
        if (setupData.locationType === 'single') {
          if (!setupData.singleLocationEmployeeCount || setupData.singleLocationEmployeeCount < 1) {
            toast({
              title: 'Validation Error',
              description: 'Please enter employee count',
              variant: 'destructive',
            });
            return false;
          }
        } else {
          // Multi-location validation
          if (!setupData.locationEmployeeCounts || setupData.locationEmployeeCounts.length === 0) {
            toast({
              title: 'Validation Error',
              description: 'Please enter employee counts for all locations',
              variant: 'destructive',
            });
            return false;
          }
        }
        return true;

      case 3:
        if (!setupData.paymentMethods || setupData.paymentMethods.length === 0) {
          toast({
            title: 'Validation Error',
            description: 'Please select at least one payment method',
            variant: 'destructive',
          });
          return false;
        }
        // Validate W-2/1099 selection for each method
        for (const method of setupData.paymentMethods) {
          if (!method.type) {
            toast({
              title: 'Validation Error',
              description: `Please select W-2 or 1099/Cash for ${PAYMENT_METHODS.find(m => m.value === method.method)?.label}`,
              variant: 'destructive',
            });
            return false;
          }
        }
        return true;

      case 4:
        if (!setupData.paymentSources || setupData.paymentSources.length === 0) {
          toast({
            title: 'Validation Error',
            description: 'Please select payment sources for all locations',
            variant: 'destructive',
          });
          return false;
        }
        // Validate percentage splits
        for (const source of setupData.paymentSources) {
          if (source.accountIds.length > 1 && source.percentages) {
            const total = Object.values(source.percentages).reduce((sum, pct) => sum + pct, 0);
            if (Math.abs(total - 100) > 0.01) {
              toast({
                title: 'Validation Error',
                description: 'Payment source percentages must total 100%',
                variant: 'destructive',
              });
              return false;
            }
          }
        }
        return true;

      case 5:
        if (!setupData.dataSourceType) {
          toast({
            title: 'Validation Error',
            description: 'Please select a data source',
            variant: 'destructive',
          });
          return false;
        }
        return true;

      case 6:
        return true; // Summary - no validation needed

      default:
        return true;
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) {
        throw new Error('Company ID not found');
      }

      // Prepare employee_counts JSONB
      let employeeCounts: any = {};
      if (setupData.locationType === 'single') {
        employeeCounts.single = setupData.singleLocationEmployeeCount;
      } else {
        setupData.locationEmployeeCounts?.forEach(loc => {
          employeeCounts[loc.locationId] = loc.count;
        });
      }

      // Prepare payment_sources JSONB
      const paymentSources: any = {};
      setupData.paymentSources?.forEach(source => {
        paymentSources[source.locationId] = {
          account_ids: source.accountIds,
          percentages: source.percentages || {},
        };
      });

      const { error } = await supabase
        .from('payroll_setup')
        .upsert({
          company_id: companyId,
          payroll_frequency: setupData.payrollFrequency,
          location_type: setupData.locationType,
          employee_counts: employeeCounts,
          multi_location_employee_count: setupData.multiLocationEmployeeCount || 0,
          payment_methods: setupData.paymentMethods || [],
          payment_sources: paymentSources,
          data_source_type: setupData.dataSourceType,
          data_source_config: setupData.dataSourceConfig || {},
        }, {
          onConflict: 'company_id',
        });

      if (error) throw error;

      // Update onboarding context
      updateSectionData('payroll', setupData);

      toast({
        title: 'Success',
        description: 'Payroll setup saved successfully',
      });
    } catch (error: any) {
      console.error('Error saving payroll setup:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save payroll configuration',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const saveAndComplete = async () => {
    setLoading(true);
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) {
        throw new Error('Company ID not found');
      }

      // Prepare employee_counts JSONB
      let employeeCounts: any = {};
      if (setupData.locationType === 'single') {
        employeeCounts.single = setupData.singleLocationEmployeeCount;
      } else {
        setupData.locationEmployeeCounts?.forEach(loc => {
          employeeCounts[loc.locationId] = loc.count;
        });
      }

      // Prepare payment_sources JSONB
      const paymentSources: any = {};
      setupData.paymentSources?.forEach(source => {
        paymentSources[source.locationId] = {
          account_ids: source.accountIds,
          percentages: source.percentages || {},
        };
      });

      const { error } = await supabase
        .from('payroll_setup')
        .upsert({
          company_id: companyId,
          payroll_frequency: setupData.payrollFrequency,
          location_type: setupData.locationType,
          employee_counts: employeeCounts,
          multi_location_employee_count: setupData.multiLocationEmployeeCount || 0,
          payment_methods: setupData.paymentMethods || [],
          payment_sources: paymentSources,
          data_source_type: setupData.dataSourceType,
          data_source_config: setupData.dataSourceConfig || {},
        }, {
          onConflict: 'company_id',
        });

      if (error) throw error;

      // Complete the section - this will auto-advance to next section or finish onboarding
      await completeSection('payroll', setupData);
      
      toast({
        title: 'Payroll Setup Complete',
        description: 'Your payroll configuration has been saved successfully',
      });
    } catch (error: any) {
      console.error('Error saving payroll setup:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save payroll configuration',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1Frequency data={setupData} onUpdate={updateSetupData} />;
      case 2:
        return <Step2EmployeeCount 
          data={setupData} 
          onUpdate={updateSetupData} 
          locations={locations}
        />;
      case 3:
        return <Step3PaymentMethods data={setupData} onUpdate={updateSetupData} />;
      case 4:
        return <Step4PaymentSources 
          data={setupData} 
          onUpdate={updateSetupData} 
          locations={locations}
          bankAccounts={bankAccounts}
        />;
      case 5:
        return <Step5DataSource data={setupData} onUpdate={updateSetupData} />;
      case 6:
        return <Step6Summary data={setupData} locations={locations} bankAccounts={bankAccounts} />;
      default:
        return null;
    }
  };

  // Show loading state while initial data is being fetched
  if (initialLoading) {
    return (
      <SectionWrapper
        title="Payroll Setup"
        description={`Step ${currentStep} of ${totalSteps}: Configure your payroll system`}
        icon={<Users className="h-6 w-6" />}
        onNext={handleNext}
        onSave={handleSave}
        showSaveButton={true}
        onSkip={() => skipSection('payroll')}
        canSkip
        nextLabel="Continue"
        isNextDisabled={true}
      >
        <div className="space-y-6">
          {/* Progress indicator skeleton */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2 flex-1">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <Skeleton key={i} className="h-2 flex-1 rounded-full" />
              ))}
            </div>
            <Skeleton className="h-4 w-12 ml-4" />
          </div>

          {/* Step content skeleton */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-5" />
                <Skeleton className="h-6 w-40" />
              </div>
              <Skeleton className="h-4 w-64 mt-2" />
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-center space-x-2">
                    <Skeleton className="h-4 w-4 rounded-full" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      title="Payroll Setup"
      description={`Step ${currentStep} of ${totalSteps}: Configure your payroll system`}
      icon={<Users className="h-6 w-6" />}
      onNext={handleNext}
      onSave={handleSave}
      showSaveButton={true}
      onSkip={() => skipSection('payroll')}
      canSkip
      nextLabel={currentStep === totalSteps ? 'Confirm & Continue' : 'Continue'}
      isNextDisabled={loading}
    >
      <div className="space-y-6">
        {/* Progress indicator */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  'h-2 flex-1 rounded-full transition-colors',
                  i + 1 <= currentStep ? 'bg-primary' : 'bg-muted'
                )}
              />
            ))}
          </div>
          <span className="text-sm text-muted-foreground">
            {currentStep} / {totalSteps}
          </span>
        </div>

        {/* Step content */}
        {renderStep()}
      </div>
    </SectionWrapper>
  );
};

// Step 1: Payroll Frequency
const Step1Frequency = ({ data, onUpdate }: { data: PayrollSetupData; onUpdate: (updates: Partial<PayrollSetupData>) => void }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Payroll Frequency
        </CardTitle>
        <CardDescription>
          How often do you run payroll?
        </CardDescription>
      </CardHeader>
      <CardContent>
        <RadioGroup
          value={data.payrollFrequency || ''}
          onValueChange={(value) => onUpdate({ payrollFrequency: value as PayrollFrequency })}
          className="grid grid-cols-2 gap-4"
        >
          {PAYROLL_FREQUENCIES.map((freq) => (
            <div key={freq.value} className="flex items-center space-x-2">
              <RadioGroupItem value={freq.value} id={freq.value} />
              <Label htmlFor={freq.value} className="cursor-pointer flex-1">
                {freq.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </CardContent>
    </Card>
  );
};

// Step 2: Employee Count & Location Assignment
const Step2EmployeeCount = ({ 
  data, 
  onUpdate, 
  locations 
}: { 
  data: PayrollSetupData; 
  onUpdate: (updates: Partial<PayrollSetupData>) => void;
  locations: any[];
}) => {
  const [locationType, setLocationType] = useState<LocationType | undefined>(data.locationType);

  useEffect(() => {
    if (locationType && locationType !== data.locationType) {
      onUpdate({ locationType });
    }
  }, [locationType]);

  if (!locationType) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Location Type
          </CardTitle>
          <CardDescription>
            Do you operate a single location or multiple locations?
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={locationType || ''}
            onValueChange={(value) => setLocationType(value as LocationType)}
            className="grid grid-cols-2 gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="single" id="single" />
              <Label htmlFor="single" className="cursor-pointer flex-1">
                Single Location
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="multi" id="multi" />
              <Label htmlFor="multi" className="cursor-pointer flex-1">
                Multi-Location
              </Label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>
    );
  }

  if (locationType === 'single') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Employee Count</CardTitle>
          <CardDescription>
            How many employees do you pay on average?
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Input
            type="number"
            min={1}
            value={data.singleLocationEmployeeCount || ''}
            onChange={(e) => onUpdate({ 
              singleLocationEmployeeCount: parseInt(e.target.value) || 0 
            })}
            placeholder="Enter number of employees"
            className="max-w-xs"
          />
        </CardContent>
      </Card>
    );
  }

  // Multi-location flow
  return (
    <div className="space-y-6">
      {/* Location-specific employees */}
      <Card>
        <CardHeader>
          <CardTitle>Location-Specific Employees</CardTitle>
          <CardDescription>
            How many employees work exclusively at each location?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {locations.map((location) => {
            const locationCount = data.locationEmployeeCounts?.find(
              lc => lc.locationId === location.id
            )?.count || 0;
            
            return (
              <div key={location.id} className="flex items-center gap-4">
                <Label className="flex-1">{location.location_name}</Label>
                <Input
                  type="number"
                  min={0}
                  value={locationCount}
                  onChange={(e) => {
                    const count = parseInt(e.target.value) || 0;
                    const existing = data.locationEmployeeCounts || [];
                    const updated = existing.filter(lc => lc.locationId !== location.id);
                    updated.push({ locationId: location.id, count });
                    onUpdate({ locationEmployeeCounts: updated });
                  }}
                  className="max-w-xs"
                />
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Multi-location employees */}
      <Card>
        <CardHeader>
          <CardTitle>Multi-Location Employees</CardTitle>
          <CardDescription>
            How many employees work across multiple locations (e.g., managers)?
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Input
            type="number"
            min={0}
            value={data.multiLocationEmployeeCount || ''}
            onChange={(e) => onUpdate({ 
              multiLocationEmployeeCount: parseInt(e.target.value) || 0 
            })}
            placeholder="Enter number of multi-location employees"
            className="max-w-xs"
          />
        </CardContent>
      </Card>

      {/* Multi-location allocation flow - simplified for now */}
      {data.multiLocationEmployeeCount && data.multiLocationEmployeeCount > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Multi-Location Employee Allocation</CardTitle>
            <CardDescription>
              For employees working across multiple locations, specify their allocation percentages.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Multi-location employee allocation details will be configured in the employee management section.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Step 3: Payment Methods
const Step3PaymentMethods = ({ 
  data, 
  onUpdate 
}: { 
  data: PayrollSetupData; 
  onUpdate: (updates: Partial<PayrollSetupData>) => void;
}) => {
  const togglePaymentMethod = (method: PaymentMethod) => {
    const existing = data.paymentMethods || [];
    const index = existing.findIndex(pm => pm.method === method);
    
    if (index >= 0) {
      // Remove if already selected
      onUpdate({ paymentMethods: existing.filter((_, i) => i !== index) });
    } else {
      // Add new (without type yet - will prompt next)
      onUpdate({ paymentMethods: [...existing, { method, type: 'w2' }] });
    }
  };

  const updatePaymentMethodType = (method: PaymentMethod, type: EmployeeType) => {
    const existing = data.paymentMethods || [];
    const updated = existing.map(pm => 
      pm.method === method ? { ...pm, type } : pm
    );
    onUpdate({ paymentMethods: updated });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Payment Methods
          </CardTitle>
          <CardDescription>
            How do you pay your employees? (Select all that apply)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {PAYMENT_METHODS.map((method) => {
            const isSelected = data.paymentMethods?.some(pm => pm.method === method.value);
            const selectedConfig = data.paymentMethods?.find(pm => pm.method === method.value);

            return (
              <div key={method.value} className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={method.value}
                    checked={isSelected}
                    onCheckedChange={() => togglePaymentMethod(method.value)}
                  />
                  <Label htmlFor={method.value} className="cursor-pointer flex-1">
                    {method.label}
                  </Label>
                </div>

                {isSelected && (
                  <div className="ml-6 p-4 bg-muted rounded-lg">
                    <Label className="text-sm font-medium mb-2 block">
                      For employees paid via {method.label}, are they W-2 or 1099/Cash?
                    </Label>
                    <RadioGroup
                      value={selectedConfig?.type || ''}
                      onValueChange={(value) => updatePaymentMethodType(method.value, value as EmployeeType)}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="w2" id={`${method.value}-w2`} />
                        <Label htmlFor={`${method.value}-w2`} className="cursor-pointer">
                          W-2
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="1099" id={`${method.value}-1099`} />
                        <Label htmlFor={`${method.value}-1099`} className="cursor-pointer">
                          1099/Cash
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

// Step 4: Payment Sources (Bank Accounts)
const Step4PaymentSources = ({ 
  data, 
  onUpdate, 
  locations, 
  bankAccounts 
}: { 
  data: PayrollSetupData; 
  onUpdate: (updates: Partial<PayrollSetupData>) => void;
  locations: any[];
  bankAccounts: any[];
}) => {
  const locationType = data.locationType || 'single';

  const updatePaymentSource = (locationId: string, accountIds: string[]) => {
    const existing = data.paymentSources || [];
    const index = existing.findIndex(ps => ps.locationId === locationId);
    
    const newSource: PaymentSourceConfig = {
      locationId,
      accountIds,
      percentages: accountIds.length > 1 ? {} : undefined,
    };

    if (index >= 0) {
      existing[index] = newSource;
    } else {
      existing.push(newSource);
    }

    onUpdate({ paymentSources: existing });
  };

  const updatePercentage = (locationId: string, accountId: string, percentage: number) => {
    const existing = data.paymentSources || [];
    const source = existing.find(ps => ps.locationId === locationId);
    if (source) {
      source.percentages = source.percentages || {};
      source.percentages[accountId] = percentage;
      onUpdate({ paymentSources: existing });
    }
  };

  if (locationType === 'single') {
    const source = data.paymentSources?.[0];
    const selectedAccountIds = source?.accountIds || [];

    return (
      <Card>
        <CardHeader>
          <CardTitle>Payment Source</CardTitle>
          <CardDescription>
            Which bank account is payroll paid from?
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select
            value={selectedAccountIds[0] || ''}
            onValueChange={(value) => updatePaymentSource('single', [value])}
          >
            <SelectTrigger className="max-w-md">
              <SelectValue placeholder="Select bank account" />
            </SelectTrigger>
            <SelectContent>
              {bankAccounts.map((account) => (
                <SelectItem key={account.id} value={account.id}>
                  {account.account_name} - {account.bank_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>
    );
  }

  // Multi-location
  return (
    <div className="space-y-6">
      {locations.map((location) => {
        const source = data.paymentSources?.find(ps => ps.locationId === location.id);
        const selectedAccountIds = source?.accountIds || [];

        return (
          <Card key={location.id}>
            <CardHeader>
              <CardTitle>{location.location_name}</CardTitle>
              <CardDescription>
                Which bank account(s) fund payroll for this location?
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                {bankAccounts.map((account) => (
                  <div key={account.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`${location.id}-${account.id}`}
                      checked={selectedAccountIds.includes(account.id)}
                      onCheckedChange={(checked) => {
                        const newIds = checked
                          ? [...selectedAccountIds, account.id]
                          : selectedAccountIds.filter(id => id !== account.id);
                        updatePaymentSource(location.id, newIds);
                      }}
                    />
                    <Label htmlFor={`${location.id}-${account.id}`} className="cursor-pointer flex-1">
                      {account.account_name} - {account.bank_name}
                    </Label>
                  </div>
                ))}
              </div>

              {selectedAccountIds.length > 1 && (
                <div className="mt-4 p-4 bg-muted rounded-lg space-y-3">
                  <Label className="text-sm font-medium">
                    Percentage Split (must total 100%)
                  </Label>
                  {selectedAccountIds.map((accountId) => {
                    const account = bankAccounts.find(a => a.id === accountId);
                    const percentage = source?.percentages?.[accountId] || 0;
                    const total = Object.values(source?.percentages || {}).reduce((sum, pct) => sum + pct, 0);
                    const isValid = Math.abs(total - 100) < 0.01;

                    return (
                      <div key={accountId} className="flex items-center gap-4">
                        <Label className="flex-1">{account?.account_name}</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min={0}
                            max={100}
                            value={percentage}
                            onChange={(e) => updatePercentage(location.id, accountId, parseFloat(e.target.value) || 0)}
                            className="w-24"
                          />
                          <span>%</span>
                        </div>
                      </div>
                    );
                  })}
                  <div className={cn(
                    "text-sm",
                    Math.abs((Object.values(source?.percentages || {}).reduce((sum, pct) => sum + pct, 0)) - 100) < 0.01
                      ? "text-green-600"
                      : "text-red-600"
                  )}>
                    Total: {formatPercent(Object.values(source?.percentages || {}).reduce((sum, pct) => sum + pct, 0))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// Step 5: Payroll Data Source
const Step5DataSource = ({ 
  data, 
  onUpdate 
}: { 
  data: PayrollSetupData; 
  onUpdate: (updates: Partial<PayrollSetupData>) => void;
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Payroll Data Source
        </CardTitle>
        <CardDescription>
          Where does your payroll data come from?
        </CardDescription>
      </CardHeader>
      <CardContent>
        <RadioGroup
          value={data.dataSourceType || ''}
          onValueChange={(value) => onUpdate({ dataSourceType: value as DataSourceType })}
          className="space-y-4"
        >
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="internal_sheet" id="internal_sheet" />
              <Label htmlFor="internal_sheet" className="cursor-pointer flex-1">
                Internal Sheet (Excel/Google Sheets)
              </Label>
            </div>
            {data.dataSourceType === 'internal_sheet' && (
              <div className="ml-6 p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground mb-4">
                  File upload functionality will be available in the next step.
                </p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="pos" id="pos" />
              <Label htmlFor="pos" className="cursor-pointer flex-1">
                Direct from POS System
              </Label>
            </div>
            {data.dataSourceType === 'pos' && (
              <div className="ml-6 p-4 bg-muted rounded-lg">
                <Label className="text-sm mb-2 block">Select POS Platform</Label>
                <Select
                  value={data.dataSourceConfig?.platform || ''}
                  onValueChange={(value) => onUpdate({ 
                    dataSourceConfig: { ...data.dataSourceConfig, platform: value } 
                  })}
                >
                  <SelectTrigger className="max-w-md">
                    <SelectValue placeholder="Select POS platform" />
                  </SelectTrigger>
                  <SelectContent>
                    {POS_PLATFORMS.map((platform) => (
                      <SelectItem key={platform.value} value={platform.value}>
                        {platform.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="payroll_service" id="payroll_service" />
              <Label htmlFor="payroll_service" className="cursor-pointer flex-1">
                Payroll Service (QuickBooks, ADP, Gusto, etc.)
              </Label>
            </div>
            {data.dataSourceType === 'payroll_service' && (
              <div className="ml-6 p-4 bg-muted rounded-lg">
                <Label className="text-sm mb-2 block">Select Payroll Service</Label>
                <Select
                  value={data.dataSourceConfig?.service || ''}
                  onValueChange={(value) => onUpdate({ 
                    dataSourceConfig: { ...data.dataSourceConfig, service: value } 
                  })}
                >
                  <SelectTrigger className="max-w-md">
                    <SelectValue placeholder="Select payroll service" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYROLL_SERVICES.map((service) => (
                      <SelectItem key={service.value} value={service.value}>
                        {service.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="other" id="other" />
              <Label htmlFor="other" className="cursor-pointer flex-1">
                Other
              </Label>
            </div>
            {data.dataSourceType === 'other' && (
              <div className="ml-6 p-4 bg-muted rounded-lg">
                <Label className="text-sm mb-2 block">
                  Describe how you currently calculate payroll
                </Label>
                <Input
                  value={data.dataSourceConfig?.description || ''}
                  onChange={(e) => onUpdate({ 
                    dataSourceConfig: { ...data.dataSourceConfig, description: e.target.value } 
                  })}
                  placeholder="Enter description..."
                  className="max-w-md"
                />
              </div>
            )}
          </div>
        </RadioGroup>
      </CardContent>
    </Card>
  );
};

// Step 6: Summary Review
const Step6Summary = ({ 
  data, 
  locations, 
  bankAccounts 
}: { 
  data: PayrollSetupData; 
  locations: any[];
  bankAccounts: any[];
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5" />
          Summary Review
        </CardTitle>
        <CardDescription>
          Please review your payroll setup configuration
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Payroll Frequency */}
        <div>
          <Label className="text-sm font-semibold">Payroll Frequency</Label>
          <p className="text-sm text-muted-foreground mt-1">
            {PAYROLL_FREQUENCIES.find(f => f.value === data.payrollFrequency)?.label || 'Not set'}
          </p>
        </div>

        {/* Employee Count */}
        <div>
          <Label className="text-sm font-semibold">Employee Count</Label>
          {data.locationType === 'single' ? (
            <p className="text-sm text-muted-foreground mt-1">
              {data.singleLocationEmployeeCount || 0} employees
            </p>
          ) : (
            <div className="mt-2 space-y-2">
              {data.locationEmployeeCounts?.map((lc) => {
                const location = locations.find(l => l.id === lc.locationId);
                return (
                  <p key={lc.locationId} className="text-sm text-muted-foreground">
                    {location?.location_name}: {lc.count} employees
                  </p>
                );
              })}
              {data.multiLocationEmployeeCount && data.multiLocationEmployeeCount > 0 && (
                <p className="text-sm text-muted-foreground">
                  Multi-location employees: {data.multiLocationEmployeeCount}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Payment Methods */}
        <div>
          <Label className="text-sm font-semibold">Payment Methods</Label>
          <div className="mt-2 space-y-1">
            {data.paymentMethods?.map((pm) => (
              <p key={pm.method} className="text-sm text-muted-foreground">
                {PAYMENT_METHODS.find(m => m.value === pm.method)?.label} - {pm.type === 'w2' ? 'W-2' : '1099/Cash'}
              </p>
            ))}
          </div>
        </div>

        {/* Payment Sources */}
        <div>
          <Label className="text-sm font-semibold">Payment Sources</Label>
          <div className="mt-2 space-y-2">
            {data.paymentSources?.map((ps) => {
              const location = locations.find(l => l.id === ps.locationId);
              const accounts = ps.accountIds.map(id => bankAccounts.find(a => a.id === id)).filter(Boolean);
              
              return (
                <div key={ps.locationId}>
                  <p className="text-sm font-medium">{location?.location_name || 'Single Location'}</p>
                  <div className="ml-4 space-y-1">
                    {accounts.map((account) => (
                      <p key={account.id} className="text-sm text-muted-foreground">
                        {account.account_name} {ps.percentages?.[account.id] && `(${ps.percentages[account.id]}%)`}
                      </p>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Data Source */}
        <div>
          <Label className="text-sm font-semibold">Data Source</Label>
          <p className="text-sm text-muted-foreground mt-1">
            {data.dataSourceType === 'internal_sheet' && 'Internal Sheet (Excel/Google Sheets)'}
            {data.dataSourceType === 'pos' && `POS System: ${POS_PLATFORMS.find(p => p.value === data.dataSourceConfig?.platform)?.label || data.dataSourceConfig?.platform}`}
            {data.dataSourceType === 'payroll_service' && `Payroll Service: ${PAYROLL_SERVICES.find(s => s.value === data.dataSourceConfig?.service)?.label || data.dataSourceConfig?.service}`}
            {data.dataSourceType === 'other' && `Other: ${data.dataSourceConfig?.description || 'Not specified'}`}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
